// src/contexts/OnboardingContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import {
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { Onboarding } from "@visitproof/types";

// Define the OnboardingContext type
interface OnboardingContextType {
  onboarding: Onboarding | null;
  loading: boolean;
  error: string | null;
  updateOnboarding: (onboarding: Partial<Onboarding>) => Promise<void>;
  clearError: () => void;
}

// Create the context
const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

// Default onboarding data
const createDefaultOnboarding = (userId: string): Onboarding => ({
  id: userId,
  showVisitsBanner: true,
  showCasesBanner: true,
  showSupervisorsBanner: true,
  showParentsBanner: true,
  showChildrenBanner: true,
  showReportsBanner: true,
  showChatBanner: true,
  showLocationsBanner: true,
  showContractsBanner: true,
  showFilesBanner: true,
  showContactsBanner: true,
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now(),
  lastUpdated: Timestamp.now(),
});

// Create a provider component
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (!user) {
      setOnboarding(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    const docRef = doc(db, `onboardings/${user.uid}`);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setLoading(false);
        if (!docSnapshot.exists()) {
          // Create default onboarding document
          const defaultOnboarding = createDefaultOnboarding(user.uid);
          setDoc(docRef, defaultOnboarding)
            .then(() => {
              setOnboarding(defaultOnboarding);
              setError(null);
            })
            .catch((err) => {
              setError(`Failed to create onboarding document: ${err.message}`);
            });
          return;
        }
        
        const onboardingData = {
          id: docSnapshot.id,
          ...docSnapshot.data(),
        } as Onboarding;
        
        setOnboarding(onboardingData);
        setError(null);
      },
      (err) => {
        setLoading(false);
        setError(`Failed to listen to onboarding changes: ${err.message}`);
      },
    );
    
    return () => {
      unsubscribe();
    };
  }, [user]);

  const updateOnboarding = useCallback(async (updatedData: Partial<Onboarding>) => {
    if (!user?.uid || !onboarding) {
      throw new Error("User or onboarding not available");
    }

    try {
      setError(null);
      const updatedOnboarding = {
        ...onboarding,
        ...updatedData,
        lastUpdated: Timestamp.now(),
      };
      
      await setDoc(doc(db, `onboardings/${user.uid}`), updatedOnboarding);
      setOnboarding(updatedOnboarding);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update onboarding';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [user?.uid, onboarding]);

  return (
    <OnboardingContext.Provider value={{ 
      onboarding, 
      loading, 
      error, 
      updateOnboarding, 
      clearError 
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};

// Custom hook for using the OnboardingContext
export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
